import { Link } from 'react-router';

import Avatar from '@/auth/components/Avatar';

import { formatRelative } from '../components/EngagementHub/format';
import type { ExpandedMode } from '../components/GovernanceApproverInbox/ApproveActions';
import { truncate } from '../components/GovernanceApproverInbox/derive';
import GovernanceDecisionControls from './GovernanceDecisionControls';
import { formatEngagementMoney, formatHours } from './money';
import { MY_GOVERNANCE_QUEUE } from './operations';

export interface GovernanceQueueRow {
  engagementId: string;
  projectName: string | null;
  /** The project's account, for an approver whose queue spans more than one. */
  accountName: string | null;
  /**
   * Null on an expertless engagement (Flow C), or when no profile resolved.
   * Flat fields rather than a nested identity object, matching
   * `ProjectEngagementRow.expertName` — the convention every live list row on
   * this branch already uses for `expertIdentity`.
   */
  expertName: string | null;
  expertPictureUrl: string | null;
  /**
   * Who opened the request, from `createdByIdentity` (ENG-5750). Null when the
   * creator has no resolvable profile; the caption then reads "Customer", as
   * the prototype's does.
   */
  creatorName: string | null;
  /** The offer scope, else the customer's request scope, else null. */
  scope: string | null;
  /** `customerConfirmedAt` — when this row entered the governance queue. */
  queuedAt: string | null;
  nteHoursTotal: number | null;
  nteTotalCents: number | null;
  currency: string | null;
}

/** Spec §9.7: the scope summary is capped per row. */
const SCOPE_TRUNCATE_CHARS = 200;

/** Spec §11.2 #6: governance pending longer than this is a red flag. */
const STALE_AFTER_MS = 3 * 24 * 60 * 60 * 1000;

/**
 * The caption a stale row carries in place of "in queue".
 *
 * Colour alone cannot say "overdue": a screen reader, and anyone who cannot
 * separate two border tints, reads a four-day row and a three-hour row
 * identically. `StaffRedFlagDots` is the precedent in this tree for giving a
 * red flag an accessible name; the staff copy for the same flag is
 * "Governance pending over 3 days" (`staffFeedViewModel.ts`), reworded here
 * because on this surface the reader IS governance.
 */
const STALE_CAPTION = 'Waiting over 3 days';

interface GovernanceInboxRowProps {
  row: GovernanceQueueRow;
  expandedMode: ExpandedMode | null;
  onExpand: (mode: ExpandedMode | null) => void;
  /** Called once the decision lands, so the parent can drop the row. */
  onDecided: (engagementId: string) => void;
  /** Pinned reference for relative-time rendering; stories and tests set it. */
  now?: Date;
}

/**
 * One queue row and its three decisions. The decisions are
 * `GovernanceDecisionControls`, which the Hub's governance banner renders too
 * (ENG-5752).
 *
 * On success the row leaves the queue — the engagement has bounced back to
 * `awaiting='customer'` (approve is the exception: it activates). That replaces
 * the prototype's keep-in-place confirmation card, which only existed because
 * nothing actually moved.
 *
 * The row is composed around the ENGAGEMENT, not the project (ENG-5753). It
 * used to be titled with `primaryProjectName` alone, so two engagements on one
 * project produced two identical rows and the approver could authorize spend on
 * the wrong one. The identity that distinguishes them is the expert plus the
 * scope, which is how the prototype's §9.7 row reads and how ENG-5730 settled
 * the detail page: the counterparty is the subject, the project is context.
 *
 * The title is a link to the engagement (ENG-5641). Approve is binding and the
 * row's figures do not say what the money buys, so the approver needs a route
 * to the full record. The prototype puts its (destination-less) button on the
 * project name instead; keeping the route on the title preserves ENG-5641's
 * 44px target and leaves the row with one navigation rather than two competing
 * with the decision buttons.
 *
 * `text-brand-primary` because that is this feature's clickable affordance
 * (`live/EngagementsPage.tsx:152`, and every link in the feature follows it).
 * A hover-only underline would have left the route invisible on touch — on the
 * one row where the next click is an irreversible financial mutation.
 */
export default function GovernanceInboxRow({
  row,
  expandedMode,
  onExpand,
  onDecided,
  now,
}: GovernanceInboxRowProps): JSX.Element {
  // An engagement can reach this queue with no expert attached (Flow C), and a
  // row with no name is the defect this ticket exists to fix — so the fallback
  // names the state rather than leaving the title blank or echoing the project.
  const expertName = row.expertName ?? 'Awaiting expert';

  // Sampled at render, and deliberately not ticking. A row left on screen as it
  // crosses the three-day line keeps its old label and treatment until something
  // re-renders it — which the queue's `cache-and-network` revalidation and the
  // post-decision `refetchQueries` both force in any session that acts on it.
  // Every relative time in this feature reads the clock the same way (ten
  // `formatRelative` call sites, no intervals anywhere), and staleness is an
  // advisory flag rather than a gate on the decision, so a per-row timer would
  // make this the only self-refreshing surface in the tree to fix a label
  // nobody is watching at the moment it turns.
  const reference = now ?? new Date();
  const queuedAt = row.queuedAt;
  const queuedMs = queuedAt === null ? Number.NaN : new Date(queuedAt).getTime();
  const ageLabel =
    queuedAt === null || Number.isNaN(queuedMs) ? null : formatRelative(queuedAt, reference);
  // No clamp: a negative difference fails the comparison on its own. Clock skew
  // surfaces one step earlier instead — `formatRelative` returns an absolute
  // date for a future timestamp (`EngagementHub/format.ts`), so a row confirmed
  // ahead of the client's clock reads as a date above the caption rather than
  // as an age. That is the same branch a row older than five weeks takes.
  const isStale = ageLabel !== null && reference.getTime() - queuedMs >= STALE_AFTER_MS;

  const scopeText = row.scope === null ? null : truncate(row.scope, SCOPE_TRUNCATE_CHARS);

  // Each line one text node rather than conditional siblings: Google Translate
  // rewrites the DOM around conditionally rendered text and crashes the tree
  // when it has element siblings (`react-google-translate` lints for it).
  const projectLine = row.projectName ?? 'No project';
  // The prototype's fallback (`InboxRow.tsx`): the role, never a blank.
  const requested = `${row.creatorName ?? 'Customer'} requested`;
  const requestedLine = row.accountName === null ? requested : `${requested} · ${row.accountName}`;

  // "NTE" is the whole point of labelling it (FB-191): both figures are ceilings
  // the approver is authorizing, not an amount already owed. A null total is a
  // resolver denial, and renders nothing rather than a $0.00 ceiling.
  //
  // The two are assembled independently, not nested, because they are not
  // gated together on the wire: `nteTotal` is side-segregated money
  // (`engagementSideMoneyField`), while `nteHoursTotal` is a plain field that is
  // null only before the engagement is priced. A viewer denied the money can
  // still be entitled to the ceiling in hours, and nesting them dropped both.
  const nteParts = [
    row.nteTotalCents === null ? null : formatEngagementMoney(row.nteTotalCents, row.currency),
    row.nteHoursTotal === null ? null : formatHours(row.nteHoursTotal),
  ].filter((part): part is string => part !== null);
  const money = nteParts.length === 0 ? null : `NTE ${nteParts.join(' · ')}`;

  return (
    <li
      className={`flex flex-col gap-16 rounded-lg border p-24 ${
        isStale
          ? 'border-yellow-700/40 bg-yellow-50 dark:bg-yellow-900/10'
          : 'border-surface-border bg-surface-card'
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-12">
        <div className="flex min-w-0 items-start gap-12">
          {/*
           * Decorative: the link beside it is the same name, so leaving the
           * avatar in the accessibility tree makes every row announce as
           * "DW, Dana Whitfield".
           */}
          <Avatar
            aria-hidden
            fullName={expertName}
            src={row.expertPictureUrl ?? undefined}
            sx={{ width: 40, height: 40 }}
          />
          {/*
           * `items-center` on the link, not on this column: the title is the
           * row's only navigation and has to carry a 44px target, which the
           * "Expert" caption beneath it must not be dragged into.
           */}
          <div className="flex min-w-0 flex-col">
            <Link
              to={`/engagements/${encodeURIComponent(row.engagementId)}`}
              className="inline-flex min-h-[44px] items-center break-words text-brand-primary underline-offset-2 subtitle-1 focus-visible:underline focus-visible:outline-none"
            >
              {expertName}
            </Link>
            <span className="text-on-surface-muted util-caption">Expert</span>
          </div>
        </div>
        {ageLabel === null ? null : (
          <div className="flex flex-col items-end gap-2">
            <span
              className={`subtitle-2 ${isStale ? 'text-yellow-900 dark:text-yellow-200' : 'text-on-surface'}`}
            >
              {ageLabel}
            </span>
            <span
              className={`util-caption ${isStale ? 'text-yellow-900 dark:text-yellow-200' : 'text-on-surface-muted'}`}
            >
              {isStale ? STALE_CAPTION : 'in queue'}
            </span>
          </div>
        )}
      </div>

      {/*
       * `min-w-0` + `break-words`: the project, account and scope are all
       * user-controlled, and the scope runs to 2000 characters on the wire. A
       * single unbroken token (a pasted URL) would otherwise push the row past
       * a 375px viewport, which the truncation cap does not prevent — it counts
       * characters, not word breaks.
       */}
      <div className="flex min-w-0 flex-col gap-8">
        {/*
         * The prototype's two lines: the project, then "<creator> requested ·
         * <account>" (`InboxRow.tsx`). `createdByIdentity` is the creator's
         * name source (ENG-5750), and the account drops on its own null.
         *
         * Same lines, not the same type: the prototype sets the project as a
         * `font-medium text-on-surface body-1` button. ENG-5753 demoted it to
         * muted `body-2` on purpose, because the expert is now the row's
         * identity and the project is secondary context.
         */}
        <div className="flex min-w-0 flex-col gap-2">
          <span className="break-words text-on-surface-muted body-2">{projectLine}</span>
          <span className="break-words text-on-surface-muted util-caption">{requestedLine}</span>
        </div>
        {scopeText === null ? null : (
          <p className="break-words text-on-surface body-2">{scopeText}</p>
        )}
      </div>

      {money === null ? null : <span className="text-on-surface hd-6">{money}</span>}

      <GovernanceDecisionControls
        engagementId={row.engagementId}
        currency={row.currency}
        expandedMode={expandedMode}
        onExpand={onExpand}
        // The mutation's selection set normalizes onto `Engagement:{id}` but
        // cannot touch the `myGovernanceQueue` array, which is what queue
        // membership actually is. The hook drops the decided row from the
        // cached array; this re-read makes the rest of the queue, and its
        // count, the server's.
        refetchQueries={[MY_GOVERNANCE_QUEUE]}
        onDecided={() => onDecided(row.engagementId)}
      />
    </li>
  );
}
